import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Building2, ChevronDown, Check, Loader } from 'lucide-react';
import firmService from '../services/firmService';
import useNotification from '../hooks/useNotification';
import Notification from './common/Notification';

const FirmSwitcher = ({ currentUser, isSuperAdmin = false }) => {
  const [firms, setFirms] = useState([]);
  const [activeFirm, setActiveFirm] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const dropdownRef = useRef(null);
  const { notification, showError, hideNotification } = useNotification();

  const loadFirms = useCallback(async () => {
    try {
      setLoading(true);
      const data = isSuperAdmin
        ? await firmService.getAllFirms()
        : await firmService.getUserFirms();
      setFirms(data);
    } catch (err) {
      console.error('Error loading firms:', err);
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    loadFirms();
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [loadFirms]);

  useEffect(() => {
    // Set active firm based on current user's active_firm_id (or primary_firm_id as fallback)
    if (currentUser && firms.length > 0) {
      const activeFirmId = currentUser.active_firm_id || currentUser.primary_firm_id;
      const active = firms.find(f => f.id === activeFirmId);
      setActiveFirm(active || firms[0]);
    }
  }, [currentUser, firms]);

  const handleSwitchFirm = async (firm) => {
    if (firm.id === activeFirm?.id) {
      setIsOpen(false);
      return;
    }

    try {
      setSwitching(true);
      await firmService.switchFirm(firm.id);
      setActiveFirm(firm);
      setIsOpen(false);

      // Reload the page to refresh all data with new firm context
      window.location.reload();
    } catch (err) {
      console.error('Error switching firm:', err);
      showError('Failed to switch firm. Please try again.');
      setSwitching(false);
    }
  };

  if (loading || !activeFirm) {
    return (
      <div className="flex items-center text-gray-400 px-3 py-2">
        <Loader className="h-4 w-4 animate-spin" />
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-200 hover:bg-white/20 rounded-md transition-colors"
        disabled={switching}
      >
        <Building2 className="h-4 w-4 text-yellow-600" />
        <span className="max-w-[150px] truncate">{activeFirm.name}</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg border border-gray-200 z-50">
          <div className="py-1" role="listbox" aria-label="Switch Firm">
            <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200">
              Switch Firm
            </div>
            {firms.map((firm) => {
              const isActive = firm.id === activeFirm?.id;
              return (
                <button
                  key={firm.id}
                  role="option"
                  aria-selected={isActive}
                  onClick={() => handleSwitchFirm(firm)}
                  disabled={switching}
                  className={`w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                    isActive ? 'bg-gray-50 text-yellow-600' : 'text-gray-700'
                  } ${switching ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                    <Building2 className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-yellow-600' : 'text-gray-400'}`} />
                    <div className="flex-1 text-left truncate">
                      <div className="font-medium truncate">{firm.name}</div>
                      {firm.twilio_phone_number && (
                        <div className="text-xs text-gray-500">{firm.twilio_phone_number}</div>
                      )}
                    </div>
                  </div>
                  {isActive && <Check className="h-4 w-4 text-yellow-600 flex-shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Switching Overlay */}
      {switching && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex items-center space-x-3">
            <Loader className="h-5 w-5 animate-spin text-yellow-600" />
            <span className="text-gray-700">Switching firm...</span>
          </div>
        </div>
      )}

      <Notification {...notification} onClose={hideNotification} />
    </div>
  );
};

export default FirmSwitcher;
